import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import RichEditor from "../components/RichEditor";
import RichView from "../components/RichView";
import {
  Plus, PencilSimple, Trash, Sparkle, RocketLaunch, CheckCircle, Clock,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { CATEGORIES, CATEGORY_LABEL, PRIORITIES, PRIORITY_LABEL } from "../lib/labels";

const SOURCES = [
  { v: "web", label: "Formulaire web" },
  { v: "email", label: "E-mail entrant" },
  { v: "internal", label: "Création interne" },
];

const DEFAULT_HTML = `<p>Bonjour <b>{client_name}</b>,</p>
<p>Nous avons bien reçu votre demande <b>#{ticket_number}</b> — <i>{ticket_subject}</i> — et notre équipe la prend en charge.</p>
<ul><li>Priorité détectée : {ticket_priority}</li><li>Catégorie : {ticket_category}</li></ul>
<p>Vous serez notifié à chaque étape. Merci de votre confiance.</p>
<p>— L'équipe Support IT</p>`;

export default function AutoResponses() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: "", active: true, categories: [], priorities: [], sources: [],
    subject: "[MasterTicket] Confirmation de votre demande #{ticket_number}",
    body_html: DEFAULT_HTML, post_as_comment: true, send_email: true,
  });
  const [aiBrief, setAiBrief] = useState("");
  const [aiBusy, setAiBusy] = useState(false);

  const load = async () => {
    const r = await api.get("/autoresponses");
    setItems(r.data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm({
      name: "", active: true, categories: [], priorities: [], sources: [],
      subject: "[MasterTicket] Confirmation de votre demande #{ticket_number}",
      body_html: DEFAULT_HTML, post_as_comment: true, send_email: true,
    });
    setOpen(true);
  };
  const openEdit = (r) => { setEditing(r); setForm({ ...r }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.patch(`/autoresponses/${editing.id}`, form);
      else await api.post("/autoresponses", form);
      toast.success(editing ? "Règle mise à jour" : "Règle créée");
      setOpen(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Supprimer « ${r.name} » ?`)) return;
    await api.delete(`/autoresponses/${r.id}`);
    toast.success("Supprimée"); load();
  };

  const toggleActive = async (r) => {
    await api.patch(`/autoresponses/${r.id}`, { active: !r.active });
    load();
  };

  const draftWithAi = async () => {
    setAiBusy(true);
    try {
      const r = await api.post("/ai/draft-autoresponse", { brief: aiBrief || `Accusé de réception pour la règle "${form.name}"`, tone: "professionnel et rassurant" });
      setForm((f) => ({ ...f, body_html: r.data.body_html || f.body_html }));
      toast.success("Message rédigé par l'IA");
    } catch (e) {
      toast.error(e.response?.data?.detail || "IA indisponible");
    } finally { setAiBusy(false); }
  };

  const toggleMulti = (field, value) => {
    setForm((f) => {
      const arr = new Set(f[field] || []);
      if (arr.has(value)) arr.delete(value); else arr.add(value);
      return { ...f, [field]: Array.from(arr) };
    });
  };

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-caps">Automatisation</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold mt-1">Prise en charge automatique</h1>
          <p className="text-sm text-muted-foreground mt-1">Envoyez des messages personnalisés dès la création d'un ticket (par e-mail et/ou en commentaire), selon la catégorie, la priorité ou la source.</p>
        </div>
        <Button data-testid="new-ar-btn" onClick={openNew}><Plus size={16} className="mr-2"/> Nouvelle règle</Button>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((r) => (
          <Card key={r.id} data-testid={`ar-${r.id}`} className={`card-hover ${!r.active ? "opacity-60" : ""}`}>
            <CardContent className="pt-6">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <RocketLaunch size={16} weight="fill" className={r.active ? "text-blue-600" : "text-muted-foreground"}/>
                    <h3 className="font-display text-lg font-semibold leading-snug">{r.name}</h3>
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    Ticket créé
                    {r.categories?.length > 0 && <> · catégories : {r.categories.map((c) => CATEGORY_LABEL[c]).join(", ")}</>}
                    {r.priorities?.length > 0 && <> · priorités : {r.priorities.map((p) => PRIORITY_LABEL[p]).join(", ")}</>}
                    {r.sources?.length > 0 && <> · sources : {r.sources.join(", ")}</>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[0.65rem] mono text-muted-foreground">{r.hits_count} déclench.</span>
                  <Switch checked={r.active} onCheckedChange={() => toggleActive(r)} data-testid={`ar-toggle-${r.id}`}/>
                </div>
              </div>
              <div className="mt-3 text-xs text-muted-foreground">
                <b>Sujet :</b> {r.subject}
              </div>
              <div className="mt-2 border rounded-md p-3 bg-muted/30 max-h-40 overflow-hidden">
                <RichView html={r.body_html}/>
              </div>
              <div className="mt-4 flex items-center gap-3 text-xs text-muted-foreground">
                {r.send_email && <span className="inline-flex items-center gap-1"><CheckCircle size={12}/> E-mail</span>}
                {r.post_as_comment && <span className="inline-flex items-center gap-1"><CheckCircle size={12}/> Commentaire</span>}
              </div>
              <div className="flex gap-2 mt-4 pt-4 border-t">
                <Button size="sm" variant="ghost" onClick={() => openEdit(r)} data-testid={`edit-ar-${r.id}`}><PencilSimple size={14}/></Button>
                <Button size="sm" variant="ghost" onClick={() => remove(r)} data-testid={`del-ar-${r.id}`}><Trash size={14}/></Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {items.length === 0 && (
          <p className="text-muted-foreground col-span-full text-center py-10">
            Aucune règle. Créez-en une pour rassurer instantanément le client dès qu'il envoie une demande.
          </p>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Modifier la règle" : "Nouvelle règle d'auto-réponse"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <Label>Nom interne</Label>
                <Input required data-testid="ar-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="Ex. Accusé standard"/>
              </div>
              <div className="flex items-end gap-2">
                <Switch id="ar-active" checked={!!form.active} onCheckedChange={(v) => setForm({...form, active: v})} data-testid="ar-active"/>
                <Label htmlFor="ar-active" className="mb-1">Actif</Label>
              </div>
            </div>
            <div>
              <Label>Sujet de l'e-mail</Label>
              <Input data-testid="ar-subject" value={form.subject} onChange={(e) => setForm({...form, subject: e.target.value})}/>
              <p className="text-xs text-muted-foreground mt-1">Variables : &#123;client_name&#125;, &#123;ticket_number&#125;, &#123;ticket_subject&#125;, &#123;ticket_priority&#125;, &#123;ticket_category&#125;</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Filter label="Catégories" testid="ar-cats" items={CATEGORIES} labels={CATEGORY_LABEL} selected={form.categories} onToggle={(v) => toggleMulti("categories", v)}/>
              <Filter label="Priorités" testid="ar-prios" items={PRIORITIES} labels={PRIORITY_LABEL} selected={form.priorities} onToggle={(v) => toggleMulti("priorities", v)}/>
              <Filter label="Sources" testid="ar-sources" items={SOURCES.map((s) => s.v)} labels={SOURCES.reduce((a,s) => ({...a,[s.v]:s.label}),{})} selected={form.sources} onToggle={(v) => toggleMulti("sources", v)}/>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <Label>Message (HTML)</Label>
                <div className="flex items-center gap-2">
                  <Input data-testid="ar-brief" className="h-8 w-64" placeholder="Brief pour l'IA (facultatif)" value={aiBrief} onChange={(e) => setAiBrief(e.target.value)}/>
                  <Button type="button" size="sm" variant="secondary" onClick={draftWithAi} disabled={aiBusy} data-testid="ar-ai">
                    <Sparkle size={14} className="mr-1.5"/>{aiBusy ? "Rédaction…" : "Rédiger avec l'IA"}
                  </Button>
                </div>
              </div>
              <RichEditor value={form.body_html} onChange={(v) => setForm({...form, body_html: v})} minHeight={220}/>
            </div>

            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Switch id="ar-email" checked={!!form.send_email} onCheckedChange={(v) => setForm({...form, send_email: v})} data-testid="ar-send-email"/>
                <Label htmlFor="ar-email">Envoyer par e-mail</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch id="ar-comment" checked={!!form.post_as_comment} onCheckedChange={(v) => setForm({...form, post_as_comment: v})} data-testid="ar-post-comment"/>
                <Label htmlFor="ar-comment">Poster en commentaire sur le ticket</Label>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="secondary" onClick={() => setOpen(false)}>Annuler</Button>
              <Button type="submit" data-testid="ar-save">{editing ? "Enregistrer" : "Créer"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Filter({ label, items, labels, selected, onToggle, testid }) {
  return (
    <div>
      <div className="label-caps mb-2">{label} · {selected?.length ? selected.length : "tous"}</div>
      <div className="flex flex-wrap gap-1.5" data-testid={testid}>
        {items.map((k) => {
          const on = selected?.includes(k);
          return (
            <button
              type="button" key={k}
              onClick={() => onToggle(k)}
              data-testid={`${testid}-${k}`}
              className={`px-2 py-1 rounded-full text-[0.7rem] border ${on ? "bg-primary text-primary-foreground border-primary" : "bg-background hover:bg-muted"}`}
            >
              {labels[k]}
            </button>
          );
        })}
      </div>
    </div>
  );
}
